import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const SolutionsPortfolio = ({ businessProfile, products, onProductSave, saving }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    status: 'prototype',
    problem_statement: '',
    solution_summary: '',
    technical_details: '',
    technologies_used: '',
    target_users: '',
    implementation_timeline: '',
    demo_url: '',
    repository_url: '',
    documentation_url: '',
    is_open_source: false
  });
  const [errors, setErrors] = useState({});

  const productCategories = [
    { value: 'technology', label: 'Technology' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'energy', label: 'Energy' },
    { value: 'water_management', label: 'Water Management' },
    { value: 'waste_management', label: 'Waste Management' },
    { value: 'governance', label: 'Governance' },
    { value: 'education', label: 'Education' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'safety_security', label: 'Safety & Security' },
    { value: 'economic_development', label: 'Economic Development' }
  ];

  const productStatuses = [
    { value: 'prototype', label: 'Prototype' },
    { value: 'pilot', label: 'Pilot' },
    { value: 'deployed', label: 'Deployed' },
    { value: 'scaled', label: 'Scaled' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = 'Solution name is required';
    if (!formData?.category) newErrors.category = 'Category is required';
    if (!formData?.description?.trim()) newErrors.description = 'Description is required';
    if (!formData?.problem_statement?.trim()) newErrors.problem_statement = 'Problem statement is required';
    if (!formData?.solution_summary?.trim()) newErrors.solution_summary = 'Solution summary is required';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    const productData = {
      ...formData,
      technologies_used: formData?.technologies_used?.split(',')?.map(tech => tech?.trim())?.filter(Boolean),
      target_users: formData?.target_users?.split(',')?.map(user => user?.trim())?.filter(Boolean)
    };

    const result = await onProductSave?.(productData);
    if (result?.success) {
      setShowAddModal(false);
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      status: 'prototype',
      problem_statement: '',
      solution_summary: '',
      technical_details: '',
      technologies_used: '',
      target_users: '',
      implementation_timeline: '',
      demo_url: '',
      repository_url: '',
      documentation_url: '',
      is_open_source: false
    });
    setErrors({});
    setSelectedProduct(null);
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'prototype': return 'bg-yellow-100 text-yellow-800';
      case 'pilot': return 'bg-blue-100 text-blue-800';
      case 'deployed': return 'bg-green-100 text-green-800';
      case 'scaled': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Solutions Portfolio</h3>
          <p className="text-sm text-gray-600">
            Showcase your products, services, and solutions to attract potential partners and clients.
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Icon name="Plus" className="w-4 h-4 mr-2" />
          Add Solution
        </Button>
      </div>

      {/* Services List */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Current Services</h4>
        <div className="space-y-2">
          {businessProfile?.services?.map((service, index) => (
            <div key={index} className="flex items-center justify-between bg-white rounded p-3 border">
              <span className="text-gray-900">{service}</span>
            </div>
          ))}
          {!businessProfile?.services?.length && (
            <p className="text-gray-500 italic">No services listed. Update your company information to add services.</p>
          )}
        </div>
      </div>

      {/* Technologies List */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Core Technologies</h4>
        <div className="flex flex-wrap gap-2">
          {businessProfile?.technologies?.map((tech, index) => (
            <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
              {tech}
            </span>
          ))}
          {!businessProfile?.technologies?.length && (
            <p className="text-gray-500 italic">No technologies listed. Update your company information to add technologies.</p>
          )}
        </div>
      </div>

      {/* Products/Solutions Grid */}
      <div className="space-y-6">
        <h4 className="text-md font-medium text-gray-900">Detailed Solutions</h4>
        
        {products?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products?.map((product) => (
              <div key={product?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-3">
                  <h5 className="text-lg font-medium text-gray-900 truncate">{product?.name}</h5>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadgeColor(product?.status)}`}>
                    {product?.status}
                  </span>
                </div>
                
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">{product?.description}</p>
                
                <div className="space-y-2 mb-4">
                  <div className="text-sm">
                    <span className="font-medium text-gray-700">Category:</span>
                    <span className="ml-2 text-gray-600 capitalize">{product?.category?.replace('_', ' ')}</span>
                  </div>
                  
                  {product?.technologies_used?.length > 0 && (
                    <div className="text-sm">
                      <span className="font-medium text-gray-700">Technologies:</span>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {product?.technologies_used?.slice(0, 3)?.map((tech, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                            {tech}
                          </span>
                        ))}
                        {product?.technologies_used?.length > 3 && (
                          <span className="text-xs text-gray-500">+{product?.technologies_used?.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Icon name="Eye" className="w-4 h-4 mr-1" />
                      {product?.view_count || 0}
                    </span>
                    <span className="flex items-center">
                      <Icon name="Heart" className="w-4 h-4 mr-1" />
                      {product?.like_count || 0}
                    </span>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewProduct(product)}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg border border-dashed border-gray-300">
            <Icon name="Package" className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No solutions yet</h3>
            <p className="text-gray-500 mb-4">Start building your solutions portfolio to showcase your capabilities.</p>
            <Button onClick={() => setShowAddModal(true)}>
              Add Your First Solution
            </Button>
          </div>
        )}
      </div>

      {/* Add Solution Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Add New Solution</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Input
                    label="Solution Name *"
                    name="name"
                    value={formData?.name}
                    onChange={handleInputChange}
                    error={errors?.name}
                    placeholder="Enter solution name"
                  />
                </div>

                <div>
                  <Select
                    label="Category *"
                    name="category"
                    value={formData?.category}
                    onChange={handleInputChange}
                    options={productCategories}
                    error={errors?.category}
                    placeholder="Select category"
                  />
                </div>

                <div>
                  <Select
                    label="Status"
                    name="status"
                    value={formData?.status}
                    onChange={handleInputChange}
                    options={productStatuses}
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_open_source"
                    id="is_open_source"
                    checked={formData?.is_open_source}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_open_source" className="ml-2 text-sm text-gray-700">
                    Open Source
                  </label>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData?.description}
                  onChange={handleInputChange}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                    errors?.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Provide a detailed description of your solution..."
                />
                {errors?.description && (
                  <p className="mt-1 text-sm text-red-600">{errors?.description}</p>
                )}
              </div>

              {/* Problem & Solution */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Problem Statement *
                  </label>
                  <textarea
                    name="problem_statement"
                    value={formData?.problem_statement}
                    onChange={handleInputChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      errors?.problem_statement ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="What problem does your solution address?"
                  />
                  {errors?.problem_statement && (
                    <p className="mt-1 text-sm text-red-600">{errors?.problem_statement}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Solution Summary *
                  </label>
                  <textarea
                    name="solution_summary"
                    value={formData?.solution_summary}
                    onChange={handleInputChange}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      errors?.solution_summary ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Brief summary of your solution approach"
                  />
                  {errors?.solution_summary && (
                    <p className="mt-1 text-sm text-red-600">{errors?.solution_summary}</p>
                  )}
                </div>
              </div>

              {/* Technical Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Technical Details
                </label>
                <textarea
                  name="technical_details"
                  value={formData?.technical_details}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Technical implementation details, architecture, etc."
                />
              </div>

              {/* Technologies and Users */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Input
                    label="Technologies Used"
                    name="technologies_used"
                    value={formData?.technologies_used}
                    onChange={handleInputChange}
                    placeholder="React, Node.js, PostgreSQL (comma separated)"
                  />
                </div>

                <div>
                  <Input
                    label="Target Users"
                    name="target_users"
                    value={formData?.target_users}
                    onChange={handleInputChange}
                    placeholder="City Officials, Citizens, Businesses (comma separated)"
                  />
                </div>
              </div>

              {/* URLs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Input
                    label="Demo URL"
                    name="demo_url"
                    type="url"
                    value={formData?.demo_url}
                    onChange={handleInputChange}
                    placeholder="https://demo.yourapp.com"
                  />
                </div>

                <div>
                  <Input
                    label="Repository URL"
                    name="repository_url"
                    type="url"
                    value={formData?.repository_url}
                    onChange={handleInputChange}
                    placeholder="https://github.com/yourorg/repo"
                  />
                </div>

                <div>
                  <Input
                    label="Documentation URL"
                    name="documentation_url"
                    type="url"
                    value={formData?.documentation_url}
                    onChange={handleInputChange}
                    placeholder="https://docs.yourapp.com"
                  />
                </div>
              </div>

              {/* Implementation Timeline */}
              <div>
                <Input
                  label="Implementation Timeline"
                  name="implementation_timeline"
                  value={formData?.implementation_timeline}
                  onChange={handleInputChange}
                  placeholder="3-6 months for pilot implementation"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-4 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Adding...' : 'Add Solution'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedProduct?.name}</h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadgeColor(selectedProduct?.status)}`}>
                    {selectedProduct?.status}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                <p className="text-gray-600">{selectedProduct?.description}</p>
              </div>

              {selectedProduct?.problem_statement && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Problem Statement</h4>
                  <p className="text-gray-600">{selectedProduct?.problem_statement}</p>
                </div>
              )}

              {selectedProduct?.solution_summary && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Solution Summary</h4>
                  <p className="text-gray-600">{selectedProduct?.solution_summary}</p>
                </div>
              )}

              {selectedProduct?.technical_details && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Technical Details</h4>
                  <p className="text-gray-600 whitespace-pre-wrap">{selectedProduct?.technical_details}</p>
                </div>
              )}

              {selectedProduct?.technologies_used?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Technologies Used</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct?.technologies_used?.map((tech, index) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Category</h4>
                  <p className="text-gray-600 capitalize">{selectedProduct?.category?.replace('_', ' ')}</p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Open Source</h4>
                  <p className="text-gray-600">{selectedProduct?.is_open_source ? 'Yes' : 'No'}</p>
                </div>
              </div>

              {(selectedProduct?.demo_url || selectedProduct?.repository_url || selectedProduct?.documentation_url) && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Links</h4>
                  <div className="space-y-2">
                    {selectedProduct?.demo_url && (
                      <a
                        href={selectedProduct?.demo_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <Icon name="ExternalLink" className="w-4 h-4 mr-2" />
                        Demo
                      </a>
                    )}
                    {selectedProduct?.repository_url && (
                      <a
                        href={selectedProduct?.repository_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <Icon name="Github" className="w-4 h-4 mr-2" />
                        Repository
                      </a>
                    )}
                    {selectedProduct?.documentation_url && (
                      <a
                        href={selectedProduct?.documentation_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-blue-600 hover:text-blue-800"
                      >
                        <Icon name="FileText" className="w-4 h-4 mr-2" />
                        Documentation
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolutionsPortfolio;